/**
 * Shoppers Page - Consumer Landing
 * Classy B2C page for CI Shopper Chrome Extension
 * 
 * Minimal navigation, extension-first CTAs
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Chrome, ShieldCheck, TrendingUp, Eye, ArrowRight, Store, CheckCircle } from 'lucide-react';
import { CILogo } from '../components/CILogo';
import { LINKS } from '../config/siteLinks';

export default function ShoppersPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Minimal Consumer Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={LINKS.shoppers} className="flex items-center gap-2">
            <CILogo size={32} showText={false} theme="light" />
            <span className="font-bold text-lg">CI Shopper</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <a href="#how" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
              How it works
            </a>
            <a href="#privacy" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
              Privacy
            </a>
            <Link 
              to={LINKS.brands} 
              className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
            >
              For Merchants
            </Link>
            <a 
              href={LINKS.chromeExtensionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
              data-testid="nav-install-extension-btn"
            >
              <Chrome className="w-4 h-4" />
              Install Extension
            </a>
          </div>
        </div>
      </nav>
      
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold mb-6">
            <Chrome className="w-3.5 h-3.5" />
            Chrome Extension
          </div>
          
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
            Shop with better signals,
            <br />
            <span className="text-blue-600">not just better deals.</span>
          </h1>
          
          <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-2xl">
            CI Shopper helps you compare prices across retailers and see proof where available — so you buy with confidence.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <a 
              href={LINKS.chromeExtensionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center gap-2"
              data-testid="hero-install-extension-btn"
            >
              <Chrome className="w-5 h-5" />
              Install CI Shopper
            </a>
            <Link
              to="/"
              className="px-8 py-4 bg-white text-slate-700 border-2 border-slate-200 rounded-xl font-bold text-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              Back to Commerce Index
            </Link>
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section id="how" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-4">How it works</h2>
        <p className="text-lg text-slate-600 mb-10">
          Three steps to smarter shopping.
        </p>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-5">
              <Chrome className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">1. Install extension</h3>
            <p className="text-slate-600">
              Add CI Shopper to Chrome in one click. No account required.
            </p>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-5">
              <Eye className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">2. Browse normally</h3>
            <p className="text-slate-600">
              On supported pages, CI Shopper shows price comparisons and proof signals automatically.
            </p>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-5">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">3. Buy with confidence</h3>
            <p className="text-slate-600">
              You'll see smarter context — not just a cheaper price. Make informed decisions.
            </p>
          </div>
        </div>
      </section>
      
      {/* Features */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-4 text-center">What CI Shopper does</h2>
          <p className="text-lg text-slate-600 mb-10 text-center max-w-2xl mx-auto">
            Real signals, not gimmicks.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-1">Compare prices across retailers</h3>
                <p className="text-slate-600 text-sm">
                  See how the same product is priced at other stores — without opening a dozen tabs.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Eye className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-1">See proof where available</h3>
                <p className="text-slate-600 text-sm">
                  On partner sites, see real-time signals like views and purchases to gauge demand.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Store className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-1">Save and track watchlists</h3>
                <p className="text-slate-600 text-sm">
                  Keep an eye on products you're considering and get notified of price drops.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-1">Privacy-first design</h3>
                <p className="text-slate-600 text-sm">
                  We don't sell your browsing history. We focus on product-level signals, not you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Privacy Section */}
      <section id="privacy" className="max-w-6xl mx-auto px-6 py-16">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-bold mb-4">Privacy</h2>
          <p className="text-lg text-slate-600 mb-6 leading-relaxed">
            CI Shopper is designed to be lightweight and respectful. We do not sell personal browsing history. We focus on product-level signals — what's trending, what's priced well — not on tracking you.
          </p>
          <p className="text-slate-600 mb-4">
            <strong>What we collect:</strong> Product URLs and prices you view (to compare prices and show proof signals).
          </p>
          <p className="text-slate-600">
            <strong>What we don't collect:</strong> Your personal information, browsing history outside product pages, or anything we'd sell to third parties.
          </p>
        </div>
      </section>
      
      {/* Note for Merchants */}
      <section className="bg-slate-50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-3xl mx-auto text-center">
            <Store className="w-10 h-10 text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Are you a merchant?</h3>
            <p className="text-slate-600 mb-6">
              CI Shopper is consumer-facing. For merchant tools — Promise Health, Decision Graph, Slack approvals — visit our merchant portal.
            </p>
            <Link
              to={LINKS.brands}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Go to For Brands
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      
      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-3xl border border-slate-200 p-10 md:p-14 text-center">
          <h2 className="text-4xl font-extrabold mb-4">Install CI Shopper in 30 seconds.</h2>
          <p className="text-xl text-slate-600 mb-8">
            Compare, verify, and shop with better signals.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a 
              href={LINKS.chromeExtensionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              data-testid="footer-install-extension-btn"
            >
              <Chrome className="w-5 h-5" />
              Install Extension
            </a>
            <Link
              to={LINKS.brands}
              className="px-8 py-4 bg-white text-slate-700 border-2 border-slate-200 rounded-xl font-bold text-lg hover:bg-slate-50 transition-colors"
            >
              For Merchants
            </Link>
          </div>
        </div>
      </section>
      
      {/* Simple Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <CILogo size={24} showText={false} theme="light" />
              <span className="text-sm text-slate-600">CI Shopper by Commerce Index</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-slate-900 transition-colors">Terms</Link>
              <Link to={LINKS.brands} className="hover:text-slate-900 transition-colors">For Merchants</Link>
              <Link to="/contact" className="hover:text-slate-900 transition-colors">Support</Link>
            </div>
          </div>
          <p className="text-center text-xs text-slate-500 mt-6">
            © 2024 Commerce Index. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
